import SEO from '../components/SEO';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, TruckIcon, CreditCard, Warehouse, Package, BarChart3, Zap, Shield, Clock, Target, Users, Settings, AlertCircle, ArrowRight, Check, Phone, Mail, Calendar, Store, Building2, TrendingUp, Search, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const MotorFactorsLanding = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', branches: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `Branches: ${formData.branches}`,
        source: 'Motor Factors Landing Page'
      });
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="SAP Business One for Motor Factors | Automotive Parts ERP System"
        description="Motor factor ERP stack with Produmex WMS, EPoS, Stream delivery. VRM lookup, serial/batch tracking, real-time stock for automotive parts distributors."
        keywords="motor factor ERP, automotive parts software, SAP B1 motor factors, parts distribution software, VRM lookup"
        canonical="https://www.ascarii.com/landing/motor-factors-landing"
      />

      {/* SEO Meta Tags - Would be in Helmet */}
      
      {/* Hero Section - Centered Glass Panel Style */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/motor-factors-hero.jpeg" alt="Motor Factor Parts" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-950/70 to-slate-900/80" />
        </div>
        
        {/* Centered Content */}
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6 text-white">For UK Motor Factors</div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Integrated Platform for Motor Factors
              </h1>
              
              <p className="text-xl md:text-2xl text-sky-300 mb-8 font-medium max-w-3xl mx-auto">
                Control margin across thousands of SKUs. Real-time warehouse stock. Instant trade pricing with alternates. Route-optimised delivery with ePOD.
              </p>
              
              {/* Glass Panel with Key Features */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8 max-w-3xl mx-auto">
                <p className="text-white font-semibold mb-6 text-lg">Built for motor factor operations</p>
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  {[
                    { icon: CheckCircle2, text: "Instant trade pricing with alternates at point of sale" },
                    { icon: CheckCircle2, text: "Barcode-driven warehouse with serial/batch traceability" },
                    { icon: CheckCircle2, text: "Route optimisation with electronic proof of delivery" },
                    { icon: CheckCircle2, text: "Real-time sync between till, warehouse, and delivery" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-1" />
                      <span className="text-white text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <a href="#demo-form">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-5 text-lg font-semibold shadow-xl">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book a Demo
                  </Button>
                </a>
              </div>

              <p className="text-blue-200 text-sm">
                See how motor factors control margins and manage complex parts inventory
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Stack - Product Overview */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The Connected Motor Factor Stack</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Four best-in-class systems working as one</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-20">
            {[
              { icon: BarChart3, product: "SAP Business One", role: "Core Platform", desc: "Single source of truth for items, pricing, customers, purchasing, serial/batch tracking, and UK VAT/MTD compliance.", color: "from-blue-500 to-sky-500", link: "/sap-business-one" },
              { icon: CreditCard, product: "EPoS Now", role: "Counter POS", desc: "Fast cloud POS for trade counters. Syncs products, customers, and prices. Pushes sales back to SAP Business One automatically.", color: "from-sky-500 to-cyan-500", link: "/products/epos-now" },
              { icon: Warehouse, product: "Produmex WMS/Scan", role: "Warehouse Execution", desc: "Barcode-driven receiving, directed picking, serial/batch control. Choose Scan (light) or WMS (advanced) as you scale.", color: "from-purple-500 to-pink-500", link: "/products/produmex-wms" },
              { icon: TruckIcon, product: "Stream", role: "Transport & ePOD", desc: "Route planning, live tracking, and electronic proof of delivery. Two-way sync with SAP Business One for watertight records.", color: "from-teal-600 to-cyan-600", link: "/products/stream" }
            ].map((item, i) => (
              <a key={i} href={item.link} target="_blank" rel="noopener noreferrer">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all cursor-pointer h-full">
                  <div className="flex items-start gap-6">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">{item.product}</h3>
                      <p className="text-sm text-cyan-300 font-medium mb-2">{item.role}</p>
                      <p className="text-blue-100 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              </a>
            ))}
          </div>

          {/* How It Works - Now inside The Stack section */}
          <div className="border-t border-white/20 pt-20" id="how-it-works">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works: Goods-In to ePOD</h2>
              <p className="text-xl text-blue-100">Five steps to complete operational visibility</p>
            </div>
            <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-2 overflow-x-auto md:overflow-x-visible">
              {[
                { step: "1", title: "Goods-In & Putaway", kpi: "<5 min per pallet", desc: "Scan deliveries on handhelds. Produmex writes serials/batches/bins to SAP Business One in real-time for traceability." },
                { step: "2", title: "Counter Sale", kpi: "Trade pricing instant", desc: "EPoS Now pulls items/prices from ERP. Staff see alternates if stock short. Sale posts to SAP Business One automatically." },
                { step: "3", title: "Pick & Pack", kpi: "<3 min pick time", desc: "Barcode pick with bin locations. WMS directs optimal path through warehouse. Serial/batch captured instantly." },
                { step: "4", title: "Route Plan", kpi: "Optimised routes", desc: "Stream plans multi-drop runs efficiently. Drivers get turn-by-turn navigation and delivery sequence on mobile." },
                { step: "5", title: "ePOD & Sync", kpi: "Zero disputes", desc: "Driver captures photo/signature proof. Stream posts status and POD back to SAP Business One for invoicing records." }
              ].map((flow, i) => (
                <React.Fragment key={i}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all text-center w-full md:flex-1 md:max-w-[220px] h-full flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-white flex items-center justify-center font-bold text-xl mb-4 mx-auto flex-shrink-0">{flow.step}</div>
                    <h3 className="font-bold text-white mb-2">{flow.title}</h3>
                    <div className="inline-block px-2 py-1 bg-cyan-400/20 text-cyan-300 rounded text-xs font-semibold mb-2">{flow.kpi}</div>
                    <p className="text-blue-100 text-sm leading-relaxed flex-1">{flow.desc}</p>
                  </motion.div>
                  
                  {/* Arrow between boxes - only on desktop */}
                  {i < 4 && (
                    <ArrowRight className="hidden md:block w-8 h-8 text-cyan-300 flex-shrink-0 self-center" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Pain - PAS Framework */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Reality for Most Motor Factors</h2>
          </div>
          
          <div className="space-y-6">
            {[
              { title: "Slow Counter Lookups & Pricing Chaos", desc: "Staff hunting through systems for stock levels. Trade pricing inconsistent. Promotions not applied. Customers waiting." },
              { title: "Stockouts & Mispicks", desc: "No real-time bin visibility. Poor handling of alternates and supersessions. Wrong parts picked. Returns and complaints." },
              { title: "Paper PODs & Delivery Disputes", desc: "Van sheets lost. No proof of delivery. Customers claiming non-delivery. Invoice disputes eating into cash flow." },
              { title: "Double Entry Everywhere", desc: "Keying sales from till into ERP. Van sheets into system. Inventory adjustments. Hours wasted, errors multiplied." },
              { title: "Audit Risk & Compliance Pressure", desc: "Serial/batch tracking patchy. UK VAT/MTD records scattered. HMRC audit would expose gaps." }
            ].map((pain, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-white rounded-xl border-l-4 border-red-600 shadow-md">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{pain.title}</h3>
                  <p className="text-gray-600 text-sm">{pain.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">There's a Better Way</h3>
            <p className="text-xl text-blue-100">One connected stack eliminates these pain points. Counter, warehouse, and delivery working as one.</p>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Proven Outcomes for Motor Factors</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { metric: "-30%", outcome: "Picking Errors", desc: "Barcode-driven accuracy with bin locations" },
              { metric: "+12%", outcome: "Counter Throughput", desc: "Faster lookups and instant pricing" },
              { metric: "-25%", outcome: "Failed Deliveries", desc: "Live ETAs and customer notifications" },
              { metric: "100%", outcome: "POD Capture", desc: "Every delivery has photo/signature proof" },
              { metric: "<5 min", outcome: "Stock Lookup", desc: "Real-time bin visibility across all sites" },
              { metric: "Zero", outcome: "Double Entry", desc: "Automatic sync eliminates manual keying" }
            ].map((outcome, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-all text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{outcome.metric}</div>
                <h3 className="font-bold text-gray-900 mb-2">{outcome.outcome}</h3>
                <p className="text-gray-600 text-sm">{outcome.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Why SAP Business One for Motor Factors */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why SAP Business One for Motor Factors</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Purpose-built for automotive parts distribution, inventory complexity, and aftermarket demands</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Package,
                title: "Complex Inventory Mastery",
                desc: "Multi-warehouse, batch/serial tracking, real-time stock control. Manage thousands of SKUs across locations. Handle fast-movers and slow-movers intelligently. Optimise working capital without risking stockouts."
              },
              {
                icon: TrendingUp,
                title: "MRP & Auto-Reordering",
                desc: "Material Requirement Planning forecasts demand and generates purchase orders automatically. Ensures parts are on hand when needed. Better lead-time management, fewer supplier disruptions, leaner inventory."
              },
              {
                icon: BarChart3,
                title: "Margin & Cost Visibility",
                desc: "Track margins, COGS, and profitability by part, supplier, or customer. Identify unprofitable SKUs. Control costs in a tight-margin business. Budget and forecast with confidence."
              },
              {
                icon: CheckCircle2,
                title: "Order & Aftermarket",
                desc: "Process high volumes of orders efficiently. Handle returns and warranty claims. Track customer order histories. Build repeat business with integrated CRM. Perfect for garage and trade accounts."
              },
              {
                icon: Shield,
                title: "Quality & Traceability",
                desc: "Batch/serial tracking for compliance and recalls. Quality control workflows. Full audit trail from supplier to customer. Critical for OEM parts and regulated components."
              },
              {
                icon: Zap,
                title: "Scalable & Flexible",
                desc: "Start with core distribution. Add modules as you grow (production, service contracts, additional warehouses). Integrate with suppliers, logistics partners, and EDI. Adapts as your business evolves."
              }
            ].map((benefit, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.1 }} 
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{benefit.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-6 text-center">AscariiGo: Enterprise Solutions, SME Economics</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Target className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold mb-2">Accessible for SMEs</h4>
                  <p className="text-purple-100 text-sm">Lower upfront cost, predictable monthly subscription. No large capital expenditure barrier. Full-featured platform for smaller distributors.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold mb-2">Faster to Value</h4>
                  <p className="text-purple-100 text-sm">Pre-configured templates for parts distribution. Less time configuring, more time running your business. Faster ROI.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Cloud className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold mb-2">Cloud-Based Reliability</h4>
                  <p className="text-purple-100 text-sm">AWS hosting with 99.995% uptime. No server management. Lower IT overhead. Perfect for distributors who want to focus on parts, not IT.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <TrendingUp className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold mb-2">Grows With You</h4>
                  <p className="text-purple-100 text-sm">Add warehouses, users, SKUs, modules as you expand. Cloud-based scalability avoids costly re-implementations. Your platform evolves with your business.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FocusPoint Automotive E-Commerce Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">E-Commerce for Motor Factors</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">FocusPoint Automotive</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">AI-powered trade portal built specifically for automotive parts businesses. Handles catalog complexity, alternative parts, and supersessions natively.</p>
          </div>

          {/* Main Feature - Alternating Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-stretch mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="/focuspoint-auto/auto-image2.webp" alt="FocusPoint Automotive" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Sell Parts Online 24/7</h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                FocusPoint transforms SAP Business One into a powerful automotive eCommerce platform. Parts ordering, product succession for older models, diagram-based ordering, and intuitive order grids for dealerships - all from your SAP data.
              </p>
              <div className="space-y-3">
                {[
                  "Search any product including historical model numbers back to 1986",
                  "Product succession - newer parts compatible with older vehicles",
                  "Diagram-based ordering for consumer ease",
                  "Intuitive order pads for dealerships with options and custom builds",
                  "Real-time product availability and pricing from SAP Business One"
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* FocusPoint AI Features */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">FocusPoint AI for Automotive</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Search, title: "Smart Search & Discovery", desc: "Natural language processing and visual search. Find parts by description, diagram, or photo." },
                { icon: Users, title: "Personalised Recommendations", desc: "AI analyses purchase history to suggest relevant parts and accessories for each customer." },
                { icon: Package, title: "Automated Inventory Sync", desc: "Predictive analytics forecast demand. Real-time stock levels from SAP Business One prevent overselling." },
                { icon: BarChart3, title: "Customer Behaviour Analytics", desc: "Identify cross-sell opportunities. Track at-risk customers for retention campaigns." },
                { icon: Clock, title: "24/7 Support", desc: "AI-powered chat assistance helps customers find parts and place orders anytime." },
                { icon: Settings, title: "Order Grid Intelligence", desc: "Smart order pads for dealerships with barcode scanning and custom build configurations." }
              ].map((feature, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="flex items-start gap-4 bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Integration & Platform */}
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Built for SAP Business One</h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                FocusPoint integrates natively with SAP Business One - no third-party middleware or transaction fees. Your existing SAP data powers your automotive eCommerce instantly.
              </p>
              <div className="space-y-3">
                {[
                  "100% configurable - no custom coding required",
                  "Managed entirely from SAP Business One backend",
                  "Integrates with User Defined Objects and Fields",
                  "SAP Certified and Endorsed Application",
                  "Implementation in 4-8 weeks typical"
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <a href="/products/focuspoint" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Learn More About FocusPoint
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="/focuspoint-auto/common-image.webp" alt="FocusPoint Platform" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features by Role */}
      <section className="py-20 relative overflow-hidden" style={{ 
        backgroundImage: 'linear-gradient(rgba(249, 250, 251, 0.95), rgba(249, 250, 251, 0.95)), url(https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=1600&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Built for Every Role in Your Operation</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              { 
                role: "Counter & Branch Staff",
                icon: Users,
                color: "from-blue-500 to-sky-500",
                features: ["Instant trade pricing with volume breaks", "Alternative items when stock short", "Customer purchase history at fingertips", "Account sales with credit limits", "Promotions applied automatically"]
              },
              { 
                role: "Warehouse Team",
                icon: Warehouse,
                color: "from-purple-500 to-pink-500",
                features: ["Barcode receiving with serial/batch capture", "Directed putaway to optimal bins", "Pick-by-light accuracy with WMS", "Cycle counting without full stocktake", "SSCC labelling for pallets"]
              },
              { 
                role: "Transport & Drivers",
                icon: TruckIcon,
                color: "from-teal-600 to-cyan-600",
                features: ["Optimised multi-drop routes", "Turn-by-turn navigation", "Live ETA updates to customers", "Photo/signature ePOD capture", "Failed delivery workflow"]
              },
              { 
                role: "Finance & IT",
                icon: Settings,
                color: "from-indigo-500 to-violet-500",
                features: ["UK VAT/MTD compliant records", "Digital audit trail for serials/batches", "API-driven integrations", "Real-time financial visibility", "Single sign-on across all systems"]
              }
            ].map((group, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white rounded-xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all text-center">
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${group.color} flex items-center justify-center`}>
                    <group.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{group.role}</h3>
                </div>
                <ul className="space-y-2 inline-block text-left">
                  {group.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WMS vs Scan Choice */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose Your Warehouse Approach</h2>
            <p className="text-xl text-gray-600">Right-sized for your operation</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                product: "Produmex Scan",
                subtitle: "Lightweight Barcode Layer",
                best: "Best for: 1-3 branches, <50 picks/day per site",
                features: [
                  "Barcode receiving and putaway",
                  "Pick-to-order with bin locations",
                  "Serial/batch capture",
                  "Cycle counting",
                  "2-hour deployment"
                ],
                color: "purple",
                link: "/products/produmex-scan"
              },
              {
                product: "Produmex WMS",
                subtitle: "Advanced Warehouse Management",
                best: "Best for: Multi-depot, >50 picks/day, complex routing",
                features: [
                  "Directed putaway workflows",
                  "Wave picking and batching",
                  "Packing with SSCC labels",
                  "Route-based loading",
                  "Combined batch+serial traceability"
                ],
                color: "blue",
                link: "/products/produmex-wms"
              }
            ].map((option, i) => (
              <a key={i} href={option.link} target="_blank" rel="noopener noreferrer">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.15 }} viewport={{ once: true }} className={`bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border-2 ${option.color === 'purple' ? 'border-purple-300' : 'border-blue-300'} hover:shadow-xl transition-all cursor-pointer h-full text-center`}>
                  <div className="flex flex-col items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg ${option.color === 'purple' ? 'bg-purple-600' : 'bg-blue-600'} flex items-center justify-center`}>
                      <Warehouse className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{option.product}</h3>
                      <p className="text-sm text-gray-600">{option.subtitle}</p>
                    </div>
                  </div>
                  <p className={`text-sm font-semibold ${option.color === 'purple' ? 'text-purple-700' : 'text-blue-700'} mb-4`}>{option.best}</p>
                  <ul className="space-y-2 inline-block text-left">
                    {option.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 ${option.color === 'purple' ? 'text-purple-600' : 'text-blue-600'} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For - Enhanced with Icons */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perfect for UK Motor Factors</h2>
            <p className="text-xl text-gray-600">Whether you're single-site or multi-branch</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Store, scenario: "Single-site factors with trade counter and local delivery van" },
              { icon: Building2, scenario: "Multi-branch operations needing consistent pricing across locations" },
              { icon: TrendingUp, scenario: "Growing factors adding depots and scaling warehouse operations" },
              { icon: BarChart3, scenario: "Established distributors with 10+ branches requiring group visibility" },
              { icon: Users, scenario: "Operations with mix of walk-in retail and trade account deliveries" },
              { icon: Shield, scenario: "Factors needing UK VAT/MTD compliance with audit-ready records" }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-gray-700 text-sm flex-1">{item.scenario}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Data Flow */}
      <section className="py-20 relative overflow-hidden" style={{ 
        backgroundImage: 'linear-gradient(rgba(249, 250, 251, 0.97), rgba(249, 250, 251, 0.97)), url(https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1600&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Real-Time Data Flow</h2>
            <p className="text-xl text-gray-600">Everything stays in sync automatically</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="space-y-6">
              {[
                { from: "SAP Business One → EPoS Now", sync: "Products, customers, pricing (incl. trade prices, special prices, volume breaks)" },
                { from: "EPoS Now → SAP Business One", sync: "Counter sales, payments, customer updates" },
                { from: "SAP Business One → Stream", sync: "Sales orders for delivery, customer details, product info" },
                { from: "Stream → SAP Business One", sync: "Delivery status, ePOD (photos/signatures), failed deliveries" },
                { from: "Produmex → SAP Business One", sync: "Goods receipts, picks, inventory adjustments, serial/batch movements" },
                { from: "SAP Business One → All Systems", sync: "Real-time inventory levels, bin locations, price updates" }
              ].map((flow, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <ArrowRight className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900 mb-1">{flow.from}</p>
                    <p className="text-gray-600 text-sm">{flow.sync}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Common Questions from Motor Factor MDs</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "How does the POS integration actually work?", a: "EPoS Now connects to SAP Business One via REST API. Products, customers, and prices sync from SAP Business One to the POS. When a sale happens, it posts back to SAP Business One as a sales order or invoice. This keeps your integrated platform as the single source of truth whilst the till stays fast and simple." },
              { q: "Can it handle trade pricing and alternates?", a: "Yes. SAP Business One's price lists, special prices, and volume breaks flow to EPoS Now. Alternative items are configured in SAP Business One, so counter staff can immediately offer supersessions when the exact part is out of stock." },
              { q: "WMS or Scan - which do we need?", a: "Start with Produmex Scan if you're doing <50 picks per day per site and want quick barcode wins. Step up to Produmex WMS when you need directed workflows, wave picking, packing/SSCC labels, or route-based loading across multiple depots." },
              { q: "Is electronic POD legally valid in the UK?", a: "Yes. Electronic signatures and photos meet UK legal requirements for proof of delivery. Stream captures timestamped GPS, photos, and signatures which sync to SAP Business One for indisputable audit trail." },
              { q: "What about UK VAT and Making Tax Digital?", a: "SAP Business One is MTD-ready with digital VAT submissions to HMRC. All sales (counter and delivery) flow through B1, maintaining compliant digital records with full audit trail." },
              { q: "Can it work across multiple branches?", a: "Absolutely. SAP Business One handles multi-branch with branch transfers, centralised purchasing, and consolidated reporting. EPoS Now supports multi-store. Stream manages deliveries from any depot. Produmex WMS works across sites." },
              { q: "How long does implementation take?", a: "Typical motor factor goes live in 12-16 weeks: SAP Business One core first, then layer on POS, warehouse, and delivery. We pilot at one branch, prove it works, then roll out to remaining locations." },
              { q: "What about data migration from our current system?", a: "We extract your items, customers, pricing, and historical transactions from your existing system and migrate to SAP Business One. Typically completed during implementation phase with validation before go-live." }
            ].map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Form */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100" id="demo-form">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">See the Stack in Action</h2>
              <p className="text-xl text-gray-600 mb-8">Book a tailored demo for your motor factor operation</p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">SAP Business One Premier Partner</h4>
                    <p className="text-sm text-gray-600">15+ years implementing for UK distributors</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Motor Factor Specialists</h4>
                    <p className="text-sm text-gray-600">Proven implementations for trade counter operations</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Proven Stack</h4>
                    <p className="text-sm text-gray-600">All four systems tested and integrated</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              {submitted ? (
                <div className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
                  <p className="text-gray-600 mb-6">We'll be in touch within one business day to arrange your tailored demo.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Book Your Demo</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="John Smith" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="john@motorfactor.co.uk" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                      <input type="text" required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Your Motor Factor Name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="+44 7700 900000" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Branches *</label>
                      <Select required value={formData.branches} onValueChange={(value) => setFormData({...formData, branches: value})}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select number of branches..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 branch</SelectItem>
                          <SelectItem value="2-5">2-5 branches</SelectItem>
                          <SelectItem value="6-10">6-10 branches</SelectItem>
                          <SelectItem value="10+">10+ branches</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="flex items-start gap-3">
                        <input type="checkbox" required checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} className="mt-1 w-5 h-5 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">I agree to Ascarii contacting me and have read the <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. *</span>
                      </label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                      {loading ? 'Sending...' : 'Book Demo'}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">No obligations • Free consultation</p>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Modernise Your Motor Factor?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Join UK motor factors running their entire operation on one connected stack.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="#demo-form" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-10 py-5 text-xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Book Demo
              </a>
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Talk to an Expert
              </a>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">SAP Premier Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">UK VAT/MTD Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">15+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MotorFactorsLanding;
